import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { ACCESS_RIGHTS } from "../constants/accessRights";

// =====================================================
// #region AUTHENTICATION INNER PAGES
// =====================================================
const ResetPassword = lazy(() => import("../pages/Authentication/ResetPassword"));
const Alt404 = lazy(() => import("../pages/AuthenticationInner/Errors/Alt404"));
const Basic404 = lazy(() => import("../pages/AuthenticationInner/Errors/Basic404"));
const Error500 = lazy(() => import("../pages/AuthenticationInner/Errors/Error500"));
const LockScreen = lazy(() => import("../pages/Authentication/LockScreen"));
const Offlinepage = lazy(() => import("../pages/AuthenticationInner/Errors/Offlinepage"));
const ExpiredToken = lazy(() => import("../pages/Authentication/ExpiredToken"));
// #endregion

// =====================================================
// #region GENERAL
// =====================================================
const ComingSoon = lazy(() => import("../pages/Others/ComingSoon/ComingSoon"));
const Maintenance = lazy(() => import("../pages/Others/Maintenance/Maintenance"));
const NotFound = lazy(() => import("../pages/Others/NotFound/NotFound"));
// #endregion

// =====================================================
// #region SOCIALS
// =====================================================
const Socials_PrivacyPolicy = lazy(() => import("../pages/Socials/PrivacyPolicy/index"));
// #endregion

// =====================================================
// #region AUTHENTICATION
// =====================================================
const Login = lazy(() => import("../pages/Authentication/Login"));
const AuthPage_GLOBO = lazy(() => import("../pages/Authentication/AuthPage_GLOBO"));
const Logout = lazy(() => import("../pages/Authentication/Logout"));
const ForgetPassword = lazy(() => import("../pages/Authentication/ForgetPassword"));
const Register = lazy(() => import("../pages/Authentication/Register"));
// #endregion

// =====================================================
// #region CORE
// =====================================================
const Notifications = lazy(() => import("../pages/Core/Notification/index"));
const Security_AuditLogs = lazy(() => import("../pages/Security/AuditLogs/index"));
const Security_Roles = lazy(() => import("../pages/Security/Roles/index"));
const Security_Users = lazy(() => import("../pages/Security/Users/index"));
const Security_AccessRequests = lazy(() => import("../pages/Security/AccessRequests/index"));
// #endregion

// =====================================================
// #region GLOBO
// =====================================================
const Globo_Dashboard = lazy(() => import("../pages/Globo/Dashboards"));
const Globo_Dashboard_ActivePatients = lazy(() => import("../pages/Globo/Dashboards/ActivePatients"));
const Globo_Dashboard_NewPatients = lazy(() => import("../pages/Globo/Dashboards/NewPatients"));
const Globo_Dashboard_NewPrescriptions = lazy(() => import("../pages/Globo/Dashboards/NewPrescriptions"));
const Globo_Dashboard_ExpiringPrescriptions = lazy(() => import("../pages/Globo/Dashboards/ExpiringPrescriptions"));
const Globo_Dashboard_ExpiredPrescriptions = lazy(() => import("../pages/Globo/Dashboards/ExpiredPrescriptions"));
const Globo_Dashboard_PendingOrders = lazy(() => import("../pages/Globo/Dashboards/PendingOrders"));
const Globo_Dashboard_PendingDelivery = lazy(() => import("../pages/Globo/Dashboards/PendingDelivery"));
const Globo_Dashboard_ForDeliveryToday = lazy(() => import("../pages/Globo/Dashboards/ForDeliveryToday"));
const Globo_Dashboard_OrdersList = lazy(() => import("../pages/Globo/Dashboards/OrdersList"));

const Globo_Patients = lazy(() => import("../pages/Globo/Patients"));
const Globo_Doctors = lazy(() => import("../pages/Globo/Doctors"));

const Globo_Reports = lazy(() => import("../pages/Globo/Reports"));
const Globo_StaticData = lazy(() => import("../pages/Globo/StaticData"));
const Globo_StaticData_Diagnosis = lazy(() => import("../pages/Globo/StaticData/Diagnosis"));
const Globo_StaticData_DropOutReasons = lazy(() => import("../pages/Globo/StaticData/DropOutReasons"));
const Globo_StaticData_Hospitals = lazy(() => import("../pages/Globo/StaticData/Hospitals"));
const Globo_StaticData_PatientSegments = lazy(() => import("../pages/Globo/StaticData/PatientSegments"));
const Globo_StaticData_Principals = lazy(() => import("../pages/Globo/StaticData/Principals"));
const Globo_StaticData_Products = lazy(() => import("../pages/Globo/StaticData/Products"));
const Globo_StaticData_Programs = lazy(() => import("../pages/Globo/StaticData/Programs"));
const Globo_StaticData_PurchasingAccounts = lazy(() => import("../pages/Globo/StaticData/PurchasingAccounts"));
const Globo_StaticData_Specializations = lazy(() => import("../pages/Globo/StaticData/Specializations"));
const Globo_StaticData_TherapeuticAreas = lazy(() => import("../pages/Globo/StaticData/TherapeuticAreas"));
const Globo_StaticData_UOMs = lazy(() => import("../pages/Globo/StaticData/UOMs"));
const Globo_StaticData_TreatmentStatus = lazy(() => import("../pages/Globo/StaticData/TreatmentStatuses"));
const Globo_StaticData_PapSchemes = lazy(() => import("../pages/Globo/StaticData/PAPSchemes"));

const Globo_Transactions_AdverseEvents = lazy(() => import("../pages/Globo/Transactions/AdverseEvents"));
const Globo_Transactions_DoctorEnrollments = lazy(() => import("../pages/Globo/Transactions/DoctorEnrollments"));
const Globo_Transactions_LOARequests = lazy(() => import("../pages/Globo/Transactions/LOARequests"));
const Globo_Transactions_OrderRequests = lazy(() => import("../pages/Globo/Transactions/OrderRequests"));
const Globo_Transactions_PatientEnrollments = lazy(() => import("../pages/Globo/Transactions/PatientEnrollments"));
const Globo_Transactions_PrescriptionRequests = lazy(() => import("../pages/Globo/Transactions/PrescriptionRequests"));

const Globo_DoctorEnrollment_EISAI = lazy(() => import("../pages/Globo/DoctorEnrollment/EISAI"));
const Globo_DoctorEnrollment_JNJ = lazy(() => import("../pages/Globo/DoctorEnrollment/JNJ"));

const Globo_PatientEnrollment = lazy(() => import("../pages/Globo/PatientEnrollment"));
const Globo_PatientEnrollment_EISAI = lazy(() => import("../pages/Globo/PatientEnrollment/EISAI"));
const Globo_PatientEnrollment_JNJ = lazy(() => import("../pages/Globo/PatientEnrollment/JNJ"));
// #endregion

const authProtectedRoutes = [
  { path: "/index", component: <Globo_Dashboard /> },

  // =====================================================
  // #region SOCIALS
  // =====================================================
  { path: "/socials/privacy-policy", component: <Socials_PrivacyPolicy /> },
  // #endregion

  // =====================================================
  // #region CORE
  // =====================================================

    {
    path: "/notifications",
    component: <Notifications />,
    permissionTypeId: [],
  },

  {
    path: "/security/users",
    component: <Security_Users />,
    permissionTypeId: [ACCESS_RIGHTS.CORE_SECURITY_USERS],
  },
  {
    path: "/security/roles",
    component: <Security_Roles />,
    permissionTypeId: [ACCESS_RIGHTS.CORE_SECURITY_ROLES],
  },
  {
    path: "/security/auditLogs",
    component: <Security_AuditLogs />,
    permissionTypeId: [ACCESS_RIGHTS.CORE_SECURITY_AUDITLOGS],
  },
  {
    path: "/security/accessrequests",
    component: <Security_AccessRequests />,
    permissionTypeId: [
      ACCESS_RIGHTS.CORE_TRANSACTIONS_ACCESSREQUESTS_VIEWER,
      ACCESS_RIGHTS.CORE_TRANSACTIONS_ACCESSREQUESTS_REQUESTOR,
      ACCESS_RIGHTS.CORE_TRANSACTIONS_ACCESSREQUESTS_APPROVER,
    ],
  },
  // #endregion

  // =====================================================
  // #region GLOBO
  // =====================================================
  {
    path: "/globo/dashboard",
    component: <Globo_Dashboard />,
    permissionTypeId: [],
  },
  {
    path: "/globo/active-patients",
    component: <Globo_Dashboard_ActivePatients />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/new-patients",
    component: <Globo_Dashboard_NewPatients />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/new-prescriptions",
    component: <Globo_Dashboard_NewPrescriptions />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/prescriptions-expiring-soon",
    component: <Globo_Dashboard_ExpiringPrescriptions />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/expired-prescriptions",
    component: <Globo_Dashboard_ExpiredPrescriptions />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/pending-orders",
    component: <Globo_Dashboard_PendingOrders />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/pending-delivery",
    component: <Globo_Dashboard_PendingDelivery />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/for-delivery-today",
    component: <Globo_Dashboard_ForDeliveryToday />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/orders-list",
    component: <Globo_Dashboard_OrdersList />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/patients",
    component: <Globo_Patients />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_PATIENTS],
  },
  {
    path: "/globo/doctors",
    component: <Globo_Doctors />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_DOCTORS],
  },

  {
    path: "/globo/settings",
    component: <Globo_StaticData />,
    permissionTypeId: [
      ACCESS_RIGHTS.GLOBO_STATICDATA_DIAGNOSIS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_DROPOUTREASONS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_PATIENTSEGMENTS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_PRINCIPALS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_PRODUCTS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_PROGRAMS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_PURCHASINGACCOUNTS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_THERAPEUTICAREAS,
      ACCESS_RIGHTS.GLOBO_STATICDATA_UOMS,
    ],
  },
  {
    path: "/globo/settings/diagnosis",
    component: <Globo_StaticData_Diagnosis />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_DIAGNOSIS],
  },
  {
    path: "/globo/settings/dropout-reasons",
    component: <Globo_StaticData_DropOutReasons />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_DROPOUTREASONS],
  },
  {
    path: "/globo/settings/hospitals",
    component: <Globo_StaticData_Hospitals />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_HOSPITALS],
  },
  {
    path: "/globo/settings/patient-segments",
    component: <Globo_StaticData_PatientSegments />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_PATIENTSEGMENTS],
  },
  {
    path: "/globo/settings/principals",
    component: <Globo_StaticData_Principals />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_PRINCIPALS],
  },
  {
    path: "/globo/settings/products",
    component: <Globo_StaticData_Products />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_PRODUCTS],
  },
  {
    path: "/globo/settings/programs",
    component: <Globo_StaticData_Programs />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_PROGRAMS],
  },
  {
    path: "/globo/settings/purchasing-accounts",
    component: <Globo_StaticData_PurchasingAccounts />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_PURCHASINGACCOUNTS],
  },
  {
    path: "/globo/settings/specializations",
    component: <Globo_StaticData_Specializations />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_SPECIALIZATIONS],
  },
  {
    path: "/globo/settings/therapeutic-areas",
    component: <Globo_StaticData_TherapeuticAreas />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_THERAPEUTICAREAS],
  },
  {
    path: "/globo/settings/uoms",
    component: <Globo_StaticData_UOMs />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_UOMS],
  },
  {
    path: "/globo/settings/treatment-status",
    component: <Globo_StaticData_TreatmentStatus />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_TREATMENTSTATUSES],
  },

  {
    path: "/globo/settings/pap-schemes",
    component: <Globo_StaticData_PapSchemes />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_STATICDATA_PAPSCHEMES],
  },

  {
    path: "/globo/transactions/adverse-events",
    component: <Globo_Transactions_AdverseEvents />,
    permissionTypeId: [ACCESS_RIGHTS.GLOBO_TRANSACTIONS_ADVERSEEVENTS],
  },
  {
    path: "/globo/transactions/doctor-enrollments",
    component: <Globo_Transactions_DoctorEnrollments />,
    permissionTypeId: [
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_DOCTORENROLLMENTS_VIEWER,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_DOCTORENROLLMENTS_REQUESTOR,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_DOCTORENROLLMENTS_APPROVER,
    ],
  },
  {
    path: "/globo/transactions/loa-requests",
    component: <Globo_Transactions_LOARequests />,
    permissionTypeId: [
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_LOAREQUESTS_VIEWER,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_LOAREQUESTS_REQUESTOR,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_LOAREQUESTS_APPROVER,
    ],
  },
  {
    path: "/globo/transactions/order-requests",
    component: <Globo_Transactions_OrderRequests />,
    permissionTypeId: [
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_ORDERREQUESTS_VIEWER,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_ORDERREQUESTS_REQUESTOR,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_ORDERREQUESTS_APPROVER,
    ],
  },
  {
    path: "/globo/transactions/patient-enrollments",
    component: <Globo_Transactions_PatientEnrollments />,
    permissionTypeId: [
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PATIENTENROLLMENTS_VIEWER,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PATIENTENROLLMENTS_REQUESTOR,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PATIENTENROLLMENTS_APPROVER,
    ],
  },
  {
    path: "/globo/transactions/prescription-requests",
    component: <Globo_Transactions_PrescriptionRequests />,
    permissionTypeId: [
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PRESCRIPTIONREQUESTS_VIEWER,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PRESCRIPTIONREQUESTS_REQUESTOR,
      ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PRESCRIPTIONREQUESTS_APPROVER,
    ],
  },

  {
    path: "/globo/reports",
    component: <Globo_Reports />,
    permissionTypeId: [],
  },
  // #endregion

  // this route should be at the end of all other routes
  // eslint-disable-next-line react/display-name
  { path: "/", exact: true, component: <Navigate to="/globo/dashboard" /> },
  { path: "*", component: <Navigate to="/notfound" /> },
  // #endregion
];

const publicRoutes = [
  // Authentication Page
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <AuthPage_GLOBO /> },
  { path: "/auth-page-globo", component: <AuthPage_GLOBO /> },
  { path: "/forgot-password", component: <ForgetPassword /> },
  { path: "/register", component: <Register /> },
  { path: "/lockscreen", component: <LockScreen /> },
  { path: "/notfound", component: <NotFound /> },
  { path: "/reset-password", component: <ResetPassword /> },
  { path: "/expired-token", component: <ExpiredToken /> },

  // AuthenticationInner pages
  { path: "/auth-404-basic", component: <Basic404 /> },
  { path: "/auth-404-alt", component: <Alt404 /> },
  { path: "/auth-500", component: <Error500 /> },
  { path: "/pages-maintenance", component: <Maintenance /> },
  { path: "/pages-coming-soon", component: <ComingSoon /> },
  { path: "/auth-offline", component: <Offlinepage /> },

  // Globo Public Pages
  { path: "/doctor-enrollment/eisai", component: <Globo_DoctorEnrollment_EISAI /> },
  { path: "/doctor-enrollment/jnj", component: <Globo_DoctorEnrollment_JNJ /> },

  { path: "/patient-enrollment", component: <Globo_PatientEnrollment /> },
  { path: "/patient-enrollment/eisai", component: <Globo_PatientEnrollment_EISAI /> },
  { path: "/patient-enrollment/jnj", component: <Globo_PatientEnrollment_JNJ /> },
];

export { authProtectedRoutes, publicRoutes };

